import dayjs from "dayjs";
import type {
  MembershipPaymentData,
  SalesManager,
} from "../../services/useMembershipPaymentsQueryService";
import {
  MEMBERSHIP_LIFECYCLE_ENDED_ERROR_CODE,
  MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE,
  REFUND_ROW_IMMUTABLE_ERROR_CODE,
  REFUND_ROW_IMMUTABLE_MESSAGE,
} from "../../lib/membershipLifecycle";

/**
 * 인계 전 원수강권보다 서버가 명시한 현재 수강권을 우선한다.
 * 회원명/상품명/결제일 조합으로 수강권을 추측하면 동명이거나 재등록인 회원을 오환불할 수 있다.
 */
export const resolveSalesMembershipId = (
  record: MembershipPaymentData,
): string | undefined => {
  if (record.source === "group") {
    return (
      record.currentMemberTicketId ||
      record.currentMembershipId ||
      record.memberTicketId ||
      record.membershipId ||
      undefined
    );
  }

  return record.currentMembershipId || record.membershipId || undefined;
};

/**
 * 매출 행의 paymentAmount/cancelAmount는 각각의 원장 이벤트 금액이다. 부분 환불이 별도 행으로
 * 생성되는 구조라 원결제 행만 보고 잔액을 재계산하면 초과 환불을 열 수 있다.
 */
export const getSalesRecordRefundableAmount = (
  record: MembershipPaymentData,
): number | null => {
  const serverAmount = Number(record.refundableAmount);
  if (record.refundableAmount !== undefined && Number.isFinite(serverAmount)) {
    return Math.max(0, serverAmount);
  }

  return null;
};

export const resolveSalesRecordId = (
  record: MembershipPaymentData,
): string | undefined =>
  record.membershipPaymentId || record.salesRecordId || record._id || undefined;

/** 서버 CAS와 같이 (role, userId)로 비교한다. 판매자 없음은 role과 무관하게 같은 값이다. */
export const isSameSalesManager = (
  left?: SalesManager,
  right?: SalesManager,
): boolean => {
  if (!left || !right) return false;
  const leftUserId = left.userId?.trim() ?? "";
  const rightUserId = right.userId?.trim() ?? "";
  if (!leftUserId || !rightUserId) return !leftUserId && !rightUserId;
  return left.role === right.role && leftUserId === rightUserId;
};

/** 서버가 인계 계보를 확인해 명시한 현재 수강권만 환불 회차 반영 대상으로 인정한다. */
export const hasAuthoritativeCurrentMembership = (
  record: MembershipPaymentData,
): boolean =>
  record.source === "group"
    ? !!record.currentMemberTicketId
    : !!record.currentMembershipId;

export const formatSalesRecordDate = (value?: string): string => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "-";
};

export type SalesRefundAvailability = {
  canRefund: boolean;
  reason?: string;
};

const refundBlockReasonMessages: Record<string, string> = {
  REQUIRED_INDEXES_NOT_READY:
    "환불 준비 작업이 완료되지 않았습니다. 잠시 후 다시 확인해주세요.",
  NOT_ORIGINAL_SALES_ROW: "원결제 매출 행에서만 환불할 수 있습니다.",
  NO_REFUNDABLE_AMOUNT: "이미 전액 환불되었거나 환불 가능한 금액이 없습니다.",
  CURRENT_MEMBERSHIP_UNRESOLVED:
    "현재 수강권 연결을 확인하지 못해 환불이 잠겨 있습니다.",
  SHARED_MEMBERSHIP_REQUIRES_MEMBERSHIP_REFUND_FLOW:
    "공유 수강권은 수강권 상세에서 환불해주세요.",
  GROUP_REFUND_NOT_SUPPORTED: "그룹 수강권은 수강권 상세에서 환불해주세요.",
  // 서버 409 errorCode 와 같은 값이 사유로 올라올 수 있다. 문구는 lib/membershipLifecycle 한 곳에서
  // 가져와, 매출 목록·매출 상세·수강권 상세가 같은 상황을 다른 말로 설명하지 않게 한다.
  [MEMBERSHIP_LIFECYCLE_ENDED_ERROR_CODE]: MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE,
  [REFUND_ROW_IMMUTABLE_ERROR_CODE]: REFUND_ROW_IMMUTABLE_MESSAGE,
};

export const getSalesRefundBlockMessage = (reason?: string): string =>
  (reason && refundBlockReasonMessages[reason]) ||
  reason ||
  "이 매출은 현재 환불할 수 없습니다.";

const salesManagerEditBlockReasonMessages: Record<string, string> = {
  MEMBERSHIP_CANCELED: "취소된 수강권은 판매자를 수정할 수 없어요.",
  WITHDRAWN_MEMBER_READ_ONLY: "탈퇴 회원의 매출은 판매자를 수정할 수 없어요.",
  CENTER_MISMATCH:
    "다른 센터 결제 기록이 섞여 있어요. 관리자에게 문의해 주세요.",
  REQUIRED_INDEXES_NOT_READY:
    "지금은 판매자 수정 기능을 사용할 수 없어요.",
  TRANSFER_LINEAGE_INCOMPLETE:
    "인계 기록을 확인할 수 없어요. 관리자에게 문의해 주세요.",
  TRANSFER_LINK_MISMATCH:
    "인계 기록을 확인할 수 없어요. 관리자에게 문의해 주세요.",
  TRANSFER_CHAIN_CYCLE:
    "인계 기록을 확인할 수 없어요. 관리자에게 문의해 주세요.",
  TRANSFER_CHAIN_TOO_DEEP:
    "인계 기록을 확인할 수 없어요. 관리자에게 문의해 주세요.",
  CURRENT_MEMBERSHIP_UNRESOLVED:
    "인계 기록을 확인할 수 없어요. 관리자에게 문의해 주세요.",
  SHARED_MEMBERSHIP_UNSUPPORTED:
    "공유 이력이 있는 수강권은 판매자를 수정할 수 없어요.",
  SALES_REFUND_OPERATION_UNRESOLVED:
    "환불 처리 중이라 지금은 판매자를 수정할 수 없어요.",
  SHARED_ACCOUNTING_OPERATION_UNRESOLVED:
    "공유 회계 처리 중이라 지금은 판매자를 수정할 수 없어요.",
};

/** 서버 내부 코드를 그대로 노출하지 않고 짧은 해요체 안내로 바꾼다. */
export const getSalesManagerEditBlockMessage = (reason?: string): string =>
  (reason && salesManagerEditBlockReasonMessages[reason]) ||
  "이 매출은 현재 판매자를 수정할 수 없어요.";

export const getSalesRefundAvailability = (
  record: MembershipPaymentData,
  hasResolvedMembership: boolean,
): SalesRefundAvailability => {
  if (record.canRefund === false) {
    return {
      canRefund: false,
      reason: getSalesRefundBlockMessage(record.refundBlockReason),
    };
  }

  if (record.canRefund !== true) {
    return {
      canRefund: false,
      reason: "서버에서 이 원매출의 환불 가능 여부를 확인하지 못했습니다.",
    };
  }

  if (!resolveSalesRecordId(record)) {
    return {
      canRefund: false,
      reason: "원매출 식별자를 확인할 수 없어 환불이 잠겨 있습니다.",
    };
  }

  if (!hasAuthoritativeCurrentMembership(record)) {
    return {
      canRefund: false,
      reason: "서버에서 현재 수강권 연결을 확인하지 못해 환불이 잠겨 있습니다.",
    };
  }

  const refundableAmount = getSalesRecordRefundableAmount(record);
  if (refundableAmount === null) {
    return {
      canRefund: false,
      reason: "서버에서 환불 가능 금액을 확인하지 못했습니다.",
    };
  }

  if (record.saleStatus === "FULL_REFUND" || refundableAmount <= 0) {
    return {
      canRefund: false,
      reason: "이미 전액 환불되었거나 환불 가능한 금액이 없습니다.",
    };
  }

  if (!hasResolvedMembership) {
    return {
      canRefund: false,
      reason: "현재 수강권 연결을 확인할 수 없어 환불이 잠겨 있습니다.",
    };
  }

  return { canRefund: true };
};
